import { useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = ["medical", "school", "emotional", "other"];

export default function EmergencyButton() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [category, setCategory] = useState("emotional");
  const [description, setDescription] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/emergency", { category, description });
      setReply(data.reply);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setDescription("");
    setReply("");
    setError("");
    setCategory("emotional");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-safe left-6 md:left-auto md:right-6 z-30 tap-target inline-flex items-center px-5 py-3 text-[11px] tracking-monocle uppercase bg-[var(--oxblood)] text-[var(--bg)] shadow-lg hover:bg-[var(--oxblood-deep)] transition-colors"
        data-testid="emergency-button"
      >
        ✦ {t("app.emergencyButton")}
      </button>

      {open && (
        <div className="fixed inset-0 z-40 bg-[var(--ink)]/50 flex items-center justify-center px-4" onClick={close} data-testid="emergency-dialog">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-[var(--surface)] border border-[var(--border)] p-10 w-full max-w-lg"
          >
            <p className="label-cap text-[var(--oxblood)]">— {t("app.emergencyTitle")}</p>
            <h2 className="font-display text-3xl font-light tracking-tight mt-3">{t("app.emergencyTitle")}</h2>
            <p className="font-display-italic text-[var(--ink-soft)] mt-2">{t("app.emergencySub")}</p>

            {!reply && (
              <>
                <div className="mt-6">
                  <p className="label-cap mb-2">{t("app.emergencyCategory")}</p>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCategory(c)}
                        data-testid={`emergency-cat-${c}`}
                        className={`px-4 py-2 text-[11px] tracking-monocle uppercase border ${
                          category === c
                            ? "bg-[var(--oxblood)] text-[var(--bg)] border-[var(--oxblood)]"
                            : "border-[var(--border)] text-[var(--ink-soft)]"
                        }`}
                      >
                        {t(`app.emergencyCategories.${c}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block mt-6">
                  <span className="label-cap">{t("app.emergencyDescription")}</span>
                  <textarea
                    required
                    rows={4}
                    className="line-input resize-none"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="emergency-description"
                  />
                </label>
                {error && <p className="text-[var(--error)] text-sm mt-3">{error}</p>}
                <div className="flex justify-end gap-4 mt-8">
                  <button type="button" onClick={close} className="btn-ghost">{t("app.cancel")}</button>
                  <button type="submit" disabled={submitting || !description.trim()} className="btn-primary" data-testid="emergency-submit">
                    {submitting ? "…" : t("app.emergencySend")}
                  </button>
                </div>
              </>
            )}
            {reply && (
              <div className="mt-8" data-testid="emergency-reply">
                <p className="label-cap text-[var(--success)]">{t("app.emergencySent")}</p>
                <p className="font-display-italic text-xl mt-4 leading-snug">«{reply}»</p>
                <button onClick={close} className="btn-primary mt-8" data-testid="emergency-close">
                  ✓
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </>
  );
}
